import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, RefreshCcw, ExternalLink, Loader2, FileDown } from "lucide-react";
import { api, BACKEND_ORIGIN, getToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function WeeklyDigestModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = async (opt = 0) => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/weekly?week_offset=${opt}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load digest");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(offset); }, [open, offset]);

  const openInTab = () => {
    if (!data?.html) return;
    const blob = new Blob([data.html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/reports/weekly.pdf?week_offset=${offset}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const filename = (data?.week_start || "digest").slice(0, 10);
      a.download = `veerox_digest_${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Digest PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb] p-0 flex flex-col" data-testid="weekly-digest-modal" aria-describedby="weekly-digest-desc">
        <DialogDescription id="weekly-digest-desc" className="sr-only">
          Weekly performance digest with P&amp;L, pattern breakdown and PDF export.
        </DialogDescription>
        <DialogHeader className="p-4 border-b border-[#1f2937] flex-row flex items-center justify-between">
          <DialogTitle className="font-heading text-lg tracking-tight">
            Weekly Digest
            <span className="text-xs text-[#6b7280] ml-2 font-mono">{data?.subject || ""}</span>
          </DialogTitle>
          <div className="flex items-center gap-1">
            <select
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value))}
              className="text-xs bg-[#111827] border border-[#1f2937] rounded px-2 py-1 font-mono"
              data-testid="digest-week-select"
            >
              <option value={0}>This week</option>
              <option value={1}>Last week</option>
              <option value={2}>2 weeks ago</option>
              <option value={3}>3 weeks ago</option>
              <option value={4}>4 weeks ago</option>
            </select>
            <button
              onClick={() => load(offset)}
              disabled={loading}
              data-testid="digest-refresh-btn"
              className="w-7 h-7 rounded flex items-center justify-center border border-[#1f2937] hover:bg-[#1f2937] text-[#9ca3af] disabled:opacity-50"
              title="Regenerate"
            >
              <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={openInTab}
              disabled={!data}
              data-testid="digest-open-tab-btn"
              className="w-7 h-7 rounded flex items-center justify-center border border-[#1f2937] hover:bg-[#1f2937] text-[#9ca3af] disabled:opacity-50"
              title="Open in new tab"
            >
              <ExternalLink className="w-3 h-3" />
            </button>
            <button
              onClick={downloadPdf}
              disabled={!data || downloading}
              data-testid="digest-download-pdf-btn"
              className="h-7 px-2.5 rounded flex items-center gap-1 border border-[#a78bfa]/40 text-[#a78bfa] hover:bg-[#a78bfa]/10 text-[10px] font-mono uppercase tracking-wider disabled:opacity-50"
              title="Download PDF"
            >
              {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
              PDF
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-hidden bg-[#0a0e1a]">
          {loading ? (
            <div className="p-10 text-center text-[#9ca3af] font-mono text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-3" />
              Generating…
            </div>
          ) : data?.html ? (
            <iframe
              srcDoc={data.html}
              title="Weekly Digest"
              sandbox="allow-same-origin"
              className="w-full h-full border-0 bg-[#0a0e1a]"
              data-testid="digest-iframe"
            />
          ) : (
            <div className="p-10 text-center text-[#6b7280] font-mono text-sm">No digest data.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
